import { InteractiveContainer, PlainButton, Tooltip } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import React from "react";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useCXSignupDialog } from "@web-src/modules/cx/components/CXSignupDialog/useCXSignupDialog";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";
import { ReactComponent as AddIcon } from "./assets/add.svg";

export const ChannelTile: React.FC<{
  title: string;
  icon: React.ReactNode;
  description: string;
  isSelected: boolean;
  isConnected: boolean;
  onSelect: () => void;
  onClick: () => void;
  pillText?: string;
}> = ({
  title,
  description,
  icon,
  pillText,
  isSelected,
  isConnected,
  onSelect,
  onClick,
}) => {
  const { t } = useTranslations();
  const cxSignupDialog = useCXSignupDialog();
  const { isAnonymous } = useAnonymousSession();

  return (
    <div className="flex flex-col gap-2">
      {isSelected && (
        <span className="font-secondary leading-2 text-xs text-[#707577]">
          {t({
            id: "cx-ai-agents-page.current-preview",
            defaultMessage: "Current preview",
          })}
        </span>
      )}
      <InteractiveContainer
        className={cx(
          "border-dark-100 relative flex w-full items-center overflow-hidden rounded-xl border border-solid transition-colors",
          isSelected && "border-dark-700"
        )}
        onClick={onSelect}
      >
        <Tooltip
          renderTrigger={({ props, ref }) => (
            <PlainButton
              className="border-grey-400 hover:border-grey-600 absolute top-2.5 right-2.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-solid transition-colors"
              ref={ref}
              {...props}
              onClick={(e) => {
                e.stopPropagation();
                if (isAnonymous) {
                  cxSignupDialog?.open();
                  return;
                }
                onClick();
              }}
            >
              {isConnected ? <SettingsIcon /> : <AddIcon />}
            </PlainButton>
          )}
        >
          {isConnected
            ? t({
                id: "cx-ai-agents-page.channel-settings",
                defaultMessage: "Channel Settings",
              })
            : t({
                id: "cx-ai-agents-page.connect-channel",
                defaultMessage: "Connect Channel",
              })}
        </Tooltip>
        <div
          className="flex h-full w-[70px] shrink-0 items-center justify-center"
          style={{
            backgroundImage:
              "radial-gradient(circle, #E1E4E6 1px, #FCFCFC 1.5px)",
            backgroundSize: "8px 8px",
          }}
        >
          {icon}
        </div>
        <div className="flex min-w-0 flex-col gap-6 py-6 px-4">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="font-secondary truncate text-lg font-medium leading-[150%] text-black">
              {title}
            </span>
            <span className="font-secondary line-clamp-2 jugl__break-word overflow-hidden text-sm leading-[21px] text-[#737B7E]">
              {description}
            </span>
          </div>
          {pillText && (
            <div className="border-secondary-100 bg-secondary-50 text-dark-800 font-secondary flex h-7 w-max items-center justify-center rounded-xl border border-solid px-2 text-xs font-medium">
              {pillText}
            </div>
          )}
        </div>
      </InteractiveContainer>
    </div>
  );
};
